import { useCallback } from 'react';

import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { ErrorMessage, Gym } from 'types';
import { client } from 'utils/api-client';
import notificationHandler from 'utils/notifications';

/**
 * Dismisses the completed-setup banner. Owner-only, enforced server side.
 *
 * Not optimistic: dismissal is a deliberate one-off click on a control that then disappears,
 * so there is no flicker to hide and nothing to roll back into.
 */
export default function useDismissOnboardingBanner(
  gymId?: string | null,
): UseMutationResult<AxiosResponse<Gym>, AxiosError<ErrorMessage>, void> {
  const queryClient = useQueryClient();
  const { errorNotification } = notificationHandler();

  const dismissBanner = useCallback((): Promise<AxiosResponse<Gym>> => {
    return client.put(`gyms/${gymId}/onboarding-banner/dismiss/`);
  }, [gymId]);

  return useMutation<AxiosResponse<Gym>, AxiosError<ErrorMessage>, void>(dismissBanner, {
    onSuccess: () => {
      queryClient.invalidateQueries('gyms');
    },
    onError: () => {
      errorNotification('Unable to save your progress. Please try again.');
    },
  });
}
